import { useCallback, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { EditContentHeader } from "../../components/ui/edit-content-header";
import { useToast } from "../../components/ui/toast-context-provider";
import { Ride } from "../../queries/models/ride.model";
import { format, set } from "date-fns";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";

export interface DeleteRidesFormProps {
  rides: Ride[];
  date: Date;
  onSave: (selectedRides: Ride[]) => Promise<any>;
  onCancel?: () => void;
}

export function DeleteRidesForm({
  rides,
  date,
  onSave,
  onCancel,
}: DeleteRidesFormProps) {
  const toast = useToast();
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);

  const makeKey = (ride: Ride) => {
    if (ride.id && ride.id > 0) return `i:${ride.id}`;
    const dateIso = new Date(ride.date).toISOString();
    return `s:${ride.scheduledRideId ?? 0}:${dateIso}`;
  };

  const initialValues = {
    selectedRideKeys: [] as string[],
  };

  const [selectedRides, setSelectedRides] = useState<Ride[]>([]);

  const validationSchema = Yup.object({
    selectedRideKeys: Yup.array().required(),
  });

  const visibleRides = useMemo(() => {
    const sortedRides = [...rides].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return sortedRides.filter((ride) => {
      const rideDate = new Date(ride.date);
      return (
        rideDate.getFullYear() === date.getFullYear() &&
        rideDate.getMonth() === date.getMonth() &&
        rideDate.getDate() === date.getDate()
      );
    });
  }, [rides, date]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: (
      <div className="">Czy chcesz usunąć jazdy? ({selectedRides.length})</div>
    ),
    height: "160px",
    width: "80%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: () => {
      onSave(selectedRides);
    },
    confirmationButtonText: "Usuń",
  };

  const onSubmit = useCallback(
    (values: { selectedRideKeys: string[] }) => {
      const selectedRides = rides.filter((ride) =>
        values.selectedRideKeys.includes(makeKey(ride))
      );
      setSelectedRides(selectedRides);
      setIsDeleteConfirmationModalOpen(true);
    },
    [rides]
  );

  return (
    <div className="p-2 h-full w-full">
      {!!mobileModalConfirmationFormProps && (
        <CustomModal {...mobileModalConfirmationFormProps} />
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange
        validateOnMount
      >
        {(formik) => (
          <Form
            autoComplete="off"
            className="flex flex-column h-full w-full overflow-hidden"
          >
            <div className="formgrid px-1 mb-2 overflow-auto h-full">
              {visibleRides && (
                <div className="field col-12">
                  <label className="font-medium m-1 w-full mb-2 block">
                    Jazdy na dzień {format(date, "dd.MM.yyyy")} wybierz które
                    chcesz usunąć.
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      const allKeys = visibleRides.map((ride) => makeKey(ride));
                      const areAllSelected = allKeys.every((k) =>
                        formik.values.selectedRideKeys.includes(k)
                      );

                      formik.setFieldValue(
                        "selectedRideKeys",
                        areAllSelected ? [] : allKeys
                      );
                    }}
                    className="p-button p-button-sm p-button-text underline text-blue-600 my-2"
                  >
                    {visibleRides.every((ride) =>
                      formik.values.selectedRideKeys.includes(makeKey(ride))
                    )
                      ? "Odznacz wszystkie"
                      : "Zaznacz wszystkie"}
                  </button>
                  {visibleRides.map((ride) => {
                    const key = makeKey(ride);
                    const isSelected = formik.values.selectedRideKeys.includes(key);

                    const toggleRide = () => {
                      const selected = [...formik.values.selectedRideKeys];
                      if (isSelected) {
                        formik.setFieldValue(
                          "selectedRideKeys",
                          selected.filter((k) => k !== key)
                        );
                      } else {
                        formik.setFieldValue("selectedRideKeys", [
                          ...selected,
                          key,
                        ]);
                      }
                    };

                    return (
                      <div
                        key={`${ride.id}${ride.scheduledRideId}${ride.date}`}
                        className={`flex items-center mb-2 cursor-pointer p-2 border-round  text-normal-color`}
                        style={{
                          backgroundColor: ride.user?.color || "#f0f0f0",
                        }}
                        onClick={toggleRide}
                      >
                        <input
                          type="checkbox"
                          id={`ride-${makeKey(ride)}`}
                          name="selectedRideKeys"
                          checked={isSelected}
                          onChange={() => {}} // prevent warning, but unused because we toggle manually
                          onClick={(e) => e.stopPropagation()} // stop bubbling to parent div
                          className="mr-2"
                        />
                        <label
                          htmlFor={`ride-${makeKey(ride)}`}
                          className="cursor-pointer"
                        >
                          {`${format(new Date(ride.date), "HH:mm")} - ${
                            ride.rider?.shortName || "Brak jeźca"
                          } - 
                          ${ride.horse?.name || "Brak konia"} -
                          ${ride.rideType?.name || "Brak typu jazdy"} - 
                          ${ride.duration}h`}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <EditContentHeader
              header="Usun jazdy"
              showDeleteButton={true}
              saveButtonDisabled={formik.values.selectedRideKeys.length === 0}
              onSaveClick={() => {}}
              showSaveButton={false}
              onDeleteClick={async () => {
                if (!formik.isValid) {
                  toast.current?.show({
                    severity: "error",
                    detail: "Form invalid",
                  });
                  return;
                }
                return formik.submitForm();
              }}
              onCancelClick={() => {
                formik.resetForm();
                if (onCancel) {
                  onCancel();
                }
              }}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}
